import { readCachedUser } from "../utils/cachedUser";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";
import NotificationBell from "../components/NotificationBell";
import Icon from "../components/Icon";

const THAI_PROVINCES = [
  "กรุงเทพมหานคร","กระบี่","กาญจนบุรี","กาฬสินธุ์","กำแพงเพชร","ขอนแก่น","จันทบุรี","ฉะเชิงเทรา",
  "ชลบุรี","ชัยนาท","ชัยภูมิ","ชุมพร","เชียงราย","เชียงใหม่","ตรัง","ตราด","ตาก","นครนายก",
  "นครปฐม","นครพนม","นครราชสีมา","นครศรีธรรมราช","นครสวรรค์","นนทบุรี","นราธิวาส","น่าน",
  "บึงกาฬ","บุรีรัมย์","ปทุมธานี","ประจวบคีรีขันธ์","ปราจีนบุรี","ปัตตานี","พระนครศรีอยุธยา",
  "พะเยา","พังงา","พัทลุง","พิจิตร","พิษณุโลก","เพชรบุรี","เพชรบูรณ์","แพร่","ภูเก็ต",
  "มหาสารคาม","มุกดาหาร","แม่ฮ่องสอน","ยโสธร","ยะลา","ร้อยเอ็ด","ระนอง","ระยอง","ราชบุรี",
  "ลพบุรี","ลำปาง","ลำพูน","เลย","ศรีสะเกษ","สกลนคร","สงขลา","สตูล","สมุทรปราการ",
  "สมุทรสงคราม","สมุทรสาคร","สระแก้ว","สระบุรี","สิงห์บุรี","สุโขทัย","สุพรรณบุรี",
  "สุราษฎร์ธานี","สุรินทร์","หนองคาย","หนองบัวลำภู","อ่างทอง","อำนาจเจริญ","อุดรธานี",
  "อุตรดิตถ์","อุทัยธานี","อุบลราชธานี",
];

function getPwStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const STRENGTH_COLORS = ['#ff5555','#c77b27','#c77b27','#dc3728'];
const STRENGTH_LABELS = ['','อ่อนมาก','พอใช้','ดี','แข็งแรง'];

const THAI_BANKS = [
  { code: "kbank",  name: "ธนาคารกสิกรไทย (KBank)" },
  { code: "scb",    name: "ธนาคารไทยพาณิชย์ (SCB)" },
  { code: "bbl",    name: "ธนาคารกรุงเทพ (BBL)" },
  { code: "ktb",    name: "ธนาคารกรุงไทย (KTB)" },
  { code: "bay",    name: "ธนาคารกรุงศรีอยุธยา (BAY)" },
  { code: "tmb",    name: "ธนาคารทหารไทยธนชาต (TTB)" },
  { code: "gsb",    name: "ธนาคารออมสิน (GSB)" },
  { code: "baac",   name: "ธนาคารเพื่อการเกษตร (BAAC)" },
  { code: "ghb",    name: "ธนาคารอาคารสงเคราะห์ (GHB)" },
  { code: "uob",    name: "ธนาคารยูโอบี (UOB)" },
  { code: "lhbank", name: "ธนาคารแลนด์แอนด์เฮ้าส์ (LH Bank)" },
  { code: "cimb",   name: "ธนาคารซีไอเอ็มบีไทย (CIMB)" },
];

const css = `
*{box-sizing:border-box;margin:0;padding:0;}
  .pp{min-height:100vh;background:var(--bg);color:var(--text);font-family:'Prompt',sans-serif;}
  .pp-nav{display:flex;align-items:center;justify-content:space-between;padding:18px 40px;background:var(--surface);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:10;}
  .pp-logo{display:flex;align-items:center;}
  .pp-logo span{color:var(--text);}
  .pp-logo-img{height:80px;width:auto;mix-blend-mode:screen;filter:brightness(1.1);}
  .pp-back{padding:7px 16px;border-radius:var(--radius-full);border:1px solid var(--border);background:transparent;color:var(--text-muted);font-family:'Prompt',sans-serif;font-size:0.8rem;cursor:pointer;}
  .pp-body{max-width:560px;margin:0 auto;padding:36px 24px;}
  .pp-avatar{width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,#1a3a25,#2a5a35);display:flex;align-items:center;justify-content:center;font-size:1.8rem;font-weight:700;color:var(--accent);margin:0 auto 20px;}
  .pp-name-center{text-align:center;margin-bottom:32px;}
  .pp-name-center h2{font-size:1.2rem;font-weight:700;margin-bottom:4px;}
  .pp-name-center p{font-size:0.82rem;color:var(--text-muted);}
  .pp-section{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:24px;margin-bottom:16px;}
  .pp-sec-title{font-size:0.72rem;letter-spacing:2px;text-transform:uppercase;color:var(--text-muted);margin-bottom:16px;}
  .form-group{margin-bottom:14px;}
  .form-label{display:block;font-size:0.75rem;color:var(--text-muted);margin-bottom:6px;}
  .form-input{width:100%;padding:11px 14px;background:#f5f6f8;border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.86rem;outline:none;transition:border-color 0.2s;}
  .form-input:focus{border-color:rgba(247,71,53,0.45);}
  .form-input::placeholder{color:#a0a7b3;}
  .form-input:disabled{opacity:0.5;cursor:not-allowed;}
  .form-select{width:100%;padding:11px 14px;background:#f5f6f8;border:1px solid var(--border);border-radius:10px;color:var(--text);font-family:'Prompt',sans-serif;font-size:0.86rem;outline:none;transition:border-color 0.2s;appearance:none;cursor:pointer;}
  .form-select:focus{border-color:rgba(247,71,53,0.45);}
  .form-select option{background:#ffffff;}
  .otp-input{letter-spacing:10px;font-size:1.4rem;font-weight:700;text-align:center;}
  .pp-save{width:100%;padding:12px;border-radius:10px;border:none;background:var(--accent);color:#f5f6f8;font-family:'Prompt',sans-serif;font-size:0.9rem;font-weight:700;cursor:pointer;transition:all 0.2s;margin-top:4px;}
  .pp-save:hover{background:#e63626;}
  .pp-save:disabled{opacity:0.5;cursor:not-allowed;}
  .pp-outline{width:100%;padding:11px;border-radius:10px;border:1px solid rgba(247,71,53,0.3);background:transparent;color:var(--accent);font-family:'Prompt',sans-serif;font-size:0.88rem;font-weight:600;cursor:pointer;transition:all 0.2s;margin-top:8px;}
  .pp-outline:hover{background:rgba(247,71,53,0.07);}
  .pp-outline:disabled{opacity:0.5;cursor:not-allowed;}
  .success-msg{background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.3);border-radius:10px;padding:10px 14px;color:var(--accent);font-size:0.8rem;margin-bottom:14px;}
  .error-msg{background:rgba(255,85,85,0.1);border:1px solid rgba(255,85,85,0.3);border-radius:10px;padding:10px 14px;color:var(--danger);font-size:0.8rem;margin-bottom:14px;}
  .pw-wrap{position:relative;}
  .pw-eye{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;color:var(--text-muted);cursor:pointer;font-size:1rem;padding:0;}
  .strength-bar{display:flex;gap:4px;margin-top:6px;}
  .strength-seg{flex:1;height:3px;border-radius:2px;background:#eef0f3;transition:background 0.3s;}
  .social-notice{background:rgba(85,153,255,0.08);border:1px solid rgba(85,153,255,0.2);border-radius:10px;padding:12px 14px;font-size:0.8rem;color:#5599ff;margin-bottom:14px;}
  .bank-verified{display:inline-flex;align-items:center;gap:6px;background:rgba(247,71,53,0.1);border:1px solid rgba(247,71,53,0.3);border-radius:100px;padding:4px 12px;font-size:0.75rem;color:var(--accent);margin-bottom:14px;}
  .bank-warning{background:rgba(255,179,71,0.1);border:1px solid rgba(255,179,71,0.3);border-radius:10px;padding:10px 14px;font-size:0.78rem;color:#c77b27;margin-bottom:14px;line-height:1.6;}
  .otp-info{background:rgba(247,71,53,0.06);border:1px solid rgba(247,71,53,0.2);border-radius:10px;padding:12px 14px;font-size:0.8rem;color:var(--text-muted);margin-bottom:14px;line-height:1.6;}
  .step-indicator{display:flex;align-items:center;gap:8px;margin-bottom:18px;}
  .step-dot{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:700;flex-shrink:0;}
  .step-dot.active{background:var(--accent);color:#f5f6f8;}
  .step-dot.done{background:#1a3a25;color:var(--accent);border:1px solid rgba(247,71,53,0.4);}
  .step-dot.inactive{background:#eef0f3;color:#a0a7b3;}
  .step-line{flex:1;height:1px;background:rgba(247,71,53,0.15);}
  @media(max-width:600px){
    .pp-nav{padding:12px 14px;}
    .pp-body{padding:20px 14px;}
    .pp-section{padding:18px 16px;}
    .pp-avatar{width:60px;height:60px;font-size:1.5rem;}
    .pp-name-center h2{font-size:1.05rem;}
  }
  @media(max-width:360px){
    .pp-body{padding:16px 12px;}
    .pp-section{padding:14px 12px;}
    .pp-save{font-size:0.85rem;padding:11px;}
    .pp-outline{font-size:0.82rem;padding:10px;}
    .step-dot{width:22px;height:22px;font-size:0.65rem;}
    .otp-input{letter-spacing:6px;font-size:1.2rem;}
  }
`;

export default function ProfilePage() {
  const navigate = useNavigate();
  const user = readCachedUser();

  // Profile fields
  const [name, setName]       = useState(user.name || "");
  const [email]               = useState(user.email || "");
  const [phone, setPhone]     = useState("");
  const [address, setAddress] = useState("");
  const [bio, setBio]         = useState("");
  const [province, setProvince] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg]       = useState("");
  const [profileErr, setProfileErr]       = useState("");

  // Bank account
  const [bankCode, setBankCode]         = useState("");
  const [bankAccNum, setBankAccNum]     = useState("");
  const [bankAccName, setBankAccName]   = useState("");
  const [bankSaving, setBankSaving]     = useState(false);
  const [bankMsg, setBankMsg]           = useState("");
  const [bankErr, setBankErr]           = useState("");
  const [bankSaved, setBankSaved]       = useState(false); // มีข้อมูลบันทึกแล้ว

  // OTP password change
  const isSocialUser = auth.currentUser?.providerData?.every(p => p.providerId !== 'password');
  const [otpStep, setOtpStep]           = useState(1); // 1=send OTP, 2=verify+new pw
  const [otpCode, setOtpCode]           = useState("");
  const [newPw, setNewPw]               = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");
  const [showNewPw, setShowNewPw]       = useState(false);
  const [otpCooldown, setOtpCooldown]   = useState(0);
  const [pwLoading, setPwLoading]       = useState(false);
  const [pwMsg, setPwMsg]               = useState("");
  const [pwErr, setPwErr]               = useState("");
  const pwStrength = getPwStrength(newPw);

  const startOtpCooldown = (secs = 60) => {
    setOtpCooldown(secs);
    const t = setInterval(() => {
      setOtpCooldown(prev => { if (prev <= 1) { clearInterval(t); return 0; } return prev - 1; });
    }, 1000);
  };

  const resetPwState = () => {
    setOtpStep(1); setOtpCode(""); setNewPw(""); setConfirmNewPw("");
    setPwMsg(""); setPwErr(""); setOtpCooldown(0);
  };

  useEffect(() => {
    if (!user.uid) { navigate("/login"); return; }
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setName(d.name || "");
        setPhone(d.phone || "");
        setAddress(d.address || "");
        setBio(d.bio || "");
        setProvince(d.manualProvince || d.province || "");
        if (d.bankAccount) {
          setBankCode(d.bankAccount.bankCode || "");
          setBankAccNum(d.bankAccount.accountNumber || "");
          setBankAccName(d.bankAccount.accountName || "");
          setBankSaved(true);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Save profile ─────────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (!name.trim()) { setProfileErr("กรุณากรอกชื่อ"); return; }
    setSavingProfile(true);
    setProfileErr(""); setProfileMsg("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        name: name.trim(), phone: phone.trim(),
        address: address.trim(), bio: bio.trim(),
        manualProvince: province.trim(),     // ผู้ใช้ตั้งเอง
        ...(province.trim() ? { province: province.trim() } : {}),
      });
      const updated = { ...user, name: name.trim() };
      localStorage.setItem("klang-user", JSON.stringify(updated));
      setProfileMsg("บันทึกข้อมูลสำเร็จ");
    } catch {
      setProfileErr("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    }
    setSavingProfile(false);
  };

  // ── Save bank account ────────────────────────────────────────────────────
  const handleSaveBank = async () => {
    setBankErr(""); setBankMsg("");
    if (!bankCode)              { setBankErr("กรุณาเลือกธนาคาร"); return; }
    if (!bankAccNum.trim())     { setBankErr("กรุณากรอกเลขบัญชี"); return; }
    if (!/^\d{10,15}$/.test(bankAccNum.replace(/-/g, "")))
                                { setBankErr("เลขบัญชีไม่ถูกต้อง (10-15 หลัก)"); return; }
    if (!bankAccName.trim())    { setBankErr("กรุณากรอกชื่อบัญชี"); return; }
    setBankSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        bankAccount: {
          bankCode,
          bankName: THAI_BANKS.find(b => b.code === bankCode)?.name || bankCode,
          accountNumber: bankAccNum.replace(/-/g, "").trim(),
          accountName:   bankAccName.trim(),
          updatedAt:     new Date().toISOString(),
        },
      });
      setBankMsg("บันทึกบัญชีธนาคารสำเร็จ");
      setBankSaved(true);
    } catch {
      setBankErr("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    }
    setBankSaving(false);
  };

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (otpCooldown > 0) return;
    setPwErr(""); setPwMsg("");
    setPwLoading(true);
    try {
      const sendOtp = httpsCallable(functions, "sendOtp");
      await sendOtp({ email });
      setOtpStep(2);
      setPwMsg(`ส่ง OTP ไปที่ ${email} แล้ว — กรุณาตรวจสอบกล่องจดหมาย (และโฟลเดอร์ Spam)`);
      startOtpCooldown(60);
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("not-found") || msg.includes("ไม่พบ")) setPwErr("ไม่พบอีเมลนี้ในระบบ");
      else setPwErr("ส่ง OTP ไม่สำเร็จ กรุณาลองใหม่");
    }
    setPwLoading(false);
  };

  // ── Step 2: Verify OTP + change password ──────────────────────────────────
  const handleVerifyOtp = async () => {
    setPwErr(""); setPwMsg("");
    if (!otpCode || otpCode.length < 6) { setPwErr("กรุณากรอก OTP 6 หลัก"); return; }
    if (!newPw || newPw.length < 8)     { setPwErr("รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร"); return; }
    if (newPw !== confirmNewPw)          { setPwErr("รหัสผ่านใหม่และยืนยันไม่ตรงกัน"); return; }
    setPwLoading(true);
    try {
      const verifyOtp = httpsCallable(functions, "verifyOtpAndResetPassword");
      await verifyOtp({ email, otp: otpCode, newPassword: newPw });
      setPwMsg("เปลี่ยนรหัสผ่านสำเร็จ!");
      resetPwState();
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("unauthenticated") || msg.includes("OTP ไม่ถูกต้อง")) setPwErr("OTP ไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง");
      else if (msg.includes("deadline-exceeded") || msg.includes("หมดอายุ"))  setPwErr("OTP หมดอายุแล้ว กรุณาขอ OTP ใหม่");
      else if (msg.includes("not-found") || msg.includes("ไม่พบ OTP"))        setPwErr("ไม่พบ OTP กรุณาขอ OTP ใหม่");
      else setPwErr("เปลี่ยนรหัสผ่านไม่สำเร็จ: " + msg);
    }
    setPwLoading(false);
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;
    setPwErr(""); setPwMsg(""); setOtpCode("");
    setPwLoading(true);
    try {
      const sendOtp = httpsCallable(functions, "sendOtp");
      await sendOtp({ email });
      setPwMsg(`ส่ง OTP ใหม่ไปที่ ${email} แล้ว`);
      startOtpCooldown(60);
    } catch {
      setPwErr("ส่ง OTP ไม่สำเร็จ กรุณาลองใหม่");
    }
    setPwLoading(false);
  };

  return (
    <div className="pp">
      <style>{css}</style>
      <nav className="pp-nav">
        <div className="pp-logo" onClick={() => navigate("/home")} style={{cursor:"pointer"}}><span className="kl-wordmark">klang.</span></div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <NotificationBell />
          <button className="pp-back" onClick={() => navigate("/home")}>← กลับ</button>
        </div>
      </nav>

      <h1 className="kl-page-title">โปรไฟล์ของฉัน</h1>
      <p className="kl-page-subtitle">จัดการข้อมูลส่วนตัวและบัญชีธนาคาร</p>
      <div className="pp-body">
        <div className="pp-avatar">{(name || email || "?")[0].toUpperCase()}</div>
        <div className="pp-name-center">
          <h2>{name || "ผู้ใช้งาน"}</h2>
          <p>{email}</p>
        </div>

        {/* ── Profile section ── */}
        <div className="pp-section">
          <div className="pp-sec-title">ข้อมูลส่วนตัว</div>
          {profileErr && <div className="error-msg">{profileErr}</div>}
          {profileMsg && <div className="success-msg">{profileMsg}</div>}
          <div className="form-group">
            <label className="form-label">ชื่อ-นามสกุล</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="ชื่อของคุณ" />
          </div>
          <div className="form-group">
            <label className="form-label">อีเมล</label>
            <input className="form-input" value={email} disabled />
          </div>
          <div className="form-group">
            <label className="form-label">เบอร์โทรศัพท์</label>
            <input className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="เช่น 0812345678" />
          </div>
          <div className="form-group">
            <label className="form-label">จังหวัด</label>
            <select className="form-select" value={province} onChange={(e) => setProvince(e.target.value)}>
              <option value="">— เลือกจังหวัด —</option>
              {THAI_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">ที่อยู่สำหรับจัดส่ง</label>
            <textarea className="form-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="บ้านเลขที่ ซอย ถนน แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์" rows={3} style={{resize:'vertical'}} />
          </div>
          <div className="form-group">
            <label className="form-label">แนะนำตัว (ไม่บังคับ)</label>
            <textarea className="form-input" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="เช่น ขายของมือสองมา 3 ปี เชื่อถือได้ 100%" rows={2} style={{resize:'vertical'}} />
          </div>
          <button className="pp-save" onClick={handleSaveProfile} disabled={savingProfile}>
            {savingProfile ? "กำลังบันทึก..." : "บันทึกข้อมูล"}
          </button>
        </div>

        {/* ── Bank account section ── */}
        <div className="pp-section">
          <div className="pp-sec-title">บัญชีธนาคารสำหรับถอนเงิน</div>

          {bankSaved && !bankMsg && (
            <div className="bank-verified">บันทึกบัญชีแล้ว</div>
          )}
          <div className="bank-warning">
            กรุณากรอกชื่อบัญชีให้ตรงกับสมุดบัญชีจริง<br />
            ระบบจะใช้ข้อมูลนี้ในการโอนเงินเมื่อมีการขอถอนเงิน
          </div>

          {bankErr && <div className="error-msg">{bankErr}</div>}
          {bankMsg && <div className="success-msg">{bankMsg}</div>}

          <div className="form-group">
            <label className="form-label">ธนาคาร</label>
            <select className="form-select" value={bankCode} onChange={e => setBankCode(e.target.value)}>
              <option value="">— เลือกธนาคาร —</option>
              {THAI_BANKS.map(b => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">เลขที่บัญชี</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              value={bankAccNum}
              onChange={e => setBankAccNum(e.target.value.replace(/[^\d-]/g, ""))}
              placeholder="เช่น 1234567890"
              maxLength={20}
            />
          </div>
          <div className="form-group">
            <label className="form-label">ชื่อบัญชี (ภาษาไทย)</label>
            <input
              className="form-input"
              value={bankAccName}
              onChange={e => setBankAccName(e.target.value)}
              placeholder="ชื่อ-นามสกุล ตามสมุดบัญชี"
            />
          </div>
          <button className="pp-save" onClick={handleSaveBank} disabled={bankSaving}>
            {bankSaving ? "กำลังบันทึก..." : "บันทึกบัญชีธนาคาร"}
          </button>
        </div>

        {/* ── Change password section ── */}
        <div className="pp-section">
          <div className="pp-sec-title">เปลี่ยนรหัสผ่าน</div>

          {isSocialUser ? (
            <div className="social-notice">
              บัญชีของคุณ login ด้วย Google<br />
              ไม่มีรหัสผ่านสำหรับบัญชีประเภทนี้ — กรุณาเปลี่ยนรหัสผ่านผ่าน Google โดยตรง
            </div>
          ) : (
            <>
              {/* Step indicator */}
              <div className="step-indicator">
                <div className={`step-dot ${otpStep === 1 ? 'active' : 'done'}`}>{otpStep > 1 ? '✓' : '1'}</div>
                <div className="step-line" />
                <div className={`step-dot ${otpStep === 2 ? 'active' : 'inactive'}`}>2</div>
              </div>

              {pwErr && <div className="error-msg">{pwErr}</div>}
              {pwMsg && <div className="success-msg">{pwMsg}</div>}

              {/* Step 1: Send OTP */}
              {otpStep === 1 && (
                <>
                  <p style={{fontSize:'0.8rem',color:'#88919e',marginBottom:16,lineHeight:1.6}}>
                    ระบบจะส่งรหัส OTP ไปยัง <strong style={{color:'#242b36'}}>{email}</strong><br />
                    เพื่อยืนยันตัวตนก่อนเปลี่ยนรหัสผ่าน
                  </p>
                  <button className="pp-save" onClick={handleSendOtp}
                    disabled={pwLoading || otpCooldown > 0}>
                    {pwLoading ? "กำลังส่ง OTP..." : otpCooldown > 0 ? `ส่ง OTP ใหม่ได้ใน ${otpCooldown} วิ` : "ส่ง OTP เพื่อเปลี่ยนรหัสผ่าน"}
                  </button>
                </>
              )}

              {/* Step 2: Verify OTP + new password */}
              {otpStep === 2 && (
                <>
                  <div className="otp-info">
                    ส่ง OTP ไปที่ <strong style={{color:'#242b36'}}>{email}</strong> แล้ว<br />
                    รหัส OTP มีอายุ <strong style={{color:'#c77b27'}}>10 นาที</strong> — ตรวจสอบโฟลเดอร์ Spam ด้วย
                  </div>

                  <div className="form-group">
                    <label className="form-label">รหัส OTP 6 หลัก</label>
                    <input className="form-input otp-input" type="text" inputMode="numeric"
                      placeholder="000000" maxLength={6}
                      value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">รหัสผ่านใหม่</label>
                    <div className="pw-wrap">
                      <input className="form-input" type={showNewPw ? "text" : "password"}
                        value={newPw} onChange={(e) => setNewPw(e.target.value)}
                        placeholder="อย่างน้อย 8 ตัวอักษร" style={{ paddingRight: 40 }} />
                      <button type="button" className="pw-eye" onClick={() => setShowNewPw(!showNewPw)}>
                        {showNewPw ? <Icon name="eye-off" size={18} /> : <Icon name="eye" size={18} />}
                      </button>
                    </div>
                    {newPw && (
                      <>
                        <div className="strength-bar">
                          {[1,2,3,4].map(i => (
                            <div key={i} className="strength-seg"
                              style={{ background: i <= pwStrength ? STRENGTH_COLORS[pwStrength-1] : '#eef0f3' }} />
                          ))}
                        </div>
                        <div style={{ fontSize:"0.7rem", color: STRENGTH_COLORS[pwStrength-1]||'#a0a7b3', marginTop:3 }}>
                          {STRENGTH_LABELS[pwStrength]}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">ยืนยันรหัสผ่านใหม่</label>
                    <div className="pw-wrap">
                      <input className="form-input" type="password"
                        value={confirmNewPw} onChange={(e) => setConfirmNewPw(e.target.value)}
                        placeholder="พิมพ์รหัสผ่านใหม่อีกครั้ง"
                        style={{ paddingRight: 40, borderColor: confirmNewPw && newPw && confirmNewPw !== newPw ? 'rgba(255,85,85,0.5)' : '' }} />
                      {confirmNewPw && newPw && (
                        <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:'0.9rem' }}>
                          {confirmNewPw === newPw ? <Icon name="check" size={16} style={{ color: "#dc3728" }} /> : <Icon name="x" size={16} style={{ color: "#ff5555" }} />}
                        </span>
                      )}
                    </div>
                  </div>

                  <button className="pp-save" onClick={handleVerifyOtp}
                    disabled={pwLoading || otpCode.length < 6 || !newPw || newPw !== confirmNewPw}>
                    {pwLoading ? "กำลังยืนยัน..." : "ยืนยันและเปลี่ยนรหัสผ่าน"}
                  </button>

                  <button className="pp-outline" onClick={handleResendOtp}
                    disabled={pwLoading || otpCooldown > 0}>
                    {otpCooldown > 0 ? `ส่ง OTP ใหม่ได้ใน ${otpCooldown} วิ` : "ส่ง OTP ใหม่"}
                  </button>

                  <button onClick={resetPwState}
                    style={{display:'block',margin:'10px auto 0',background:'none',border:'none',color:'#88919e',cursor:'pointer',fontSize:'0.78rem'}}>
                    ← ยกเลิก
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
